import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import AuthenticatedImage from '../../../components/AuthenticatedImage';
import { constructionRequest } from '../../../utils/constructionApi';
import { FaPlay, FaVideo } from 'react-icons/fa';
import { isImage, isVideo, VideoPlayerModal } from './ConImageList';

/** Notes + media created within this span (ms) are shown in one card. Increase on slow Site networks. */
export const UPDATE_GROUP_WINDOW_MS = 30000;

function parseUtcMs(str) {
  if (!str) return null;
  let s = str;
  if (typeof str === 'string' && !str.includes('+') && !str.includes('Z')) {
    s = str + 'Z';
  }
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return d.getTime();
}

/**
 * Cluster notes and media by time: sorted ascending, new group when gap > windowMs from batch start.
 */
export function buildUpdateGroups(notes, images, windowMs = UPDATE_GROUP_WINDOW_MS) {
  const events = [];
  (notes || []).forEach((note) => {
    const t = parseUtcMs(note.date);
    if (t != null) events.push({ kind: 'note', t, note });
  });
  (images || []).forEach((img, index) => {
    const t = parseUtcMs(img.upload_time || img.upload_date);
    if (t != null) events.push({ kind: 'media', t, img, index });
  });
  events.sort((a, b) => a.t - b.t);

  const timedGroups = [];
  let cur = null;
  for (const e of events) {
    if (!cur) {
      cur = { startT: e.t, notes: [], mediaItems: [] };
    } else if (e.t - cur.startT > windowMs) {
      timedGroups.push(cur);
      cur = { startT: e.t, notes: [], mediaItems: [] };
    }
    if (e.kind === 'note') {
      cur.notes.push(e.note);
    } else {
      cur.mediaItems.push({ img: e.img, index: e.index });
    }
  }
  if (cur) timedGroups.push(cur);

  const undatedNotes = (notes || []).filter((n) => parseUtcMs(n.date) == null);
  const undatedMedia = [];
  (images || []).forEach((img, index) => {
    if (parseUtcMs(img.upload_time || img.upload_date) == null) {
      undatedMedia.push({ img, index });
    }
  });

  const sortedTimed = [...timedGroups].sort((a, b) => b.startT - a.startT);

  if (undatedNotes.length > 0 || undatedMedia.length > 0) {
    return [
      ...sortedTimed,
      { startT: null, notes: undatedNotes, mediaItems: undatedMedia },
    ];
  }
  return sortedTimed;
}

function formatGroupTime(startT) {
  if (startT == null) return 'Undated';
  return new Date(startT).toLocaleString('en-CA', {
    timeZone: 'America/Toronto',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const ConTaskUpdateList = ({ taskId }) => {
  const [notes, setNotes] = useState([]);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mediaError, setMediaError] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);

  const viewerContainerRef = useRef(null);
  const viewerInstanceRef = useRef(null);

  const groups = useMemo(
    () => buildUpdateGroups(notes, images, UPDATE_GROUP_WINDOW_MS),
    [notes, images]
  );

  useEffect(() => {
    if (!taskId) return;
    let cancelled = false;
    const controller = new AbortController();
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        setMediaError(null);
        const response = await constructionRequest(`/api/task/${taskId}`, { signal: controller.signal });
        if (!response.ok) throw new Error('Failed to fetch task data');
        const taskData = await response.json();
        if (cancelled) return;
        setNotes(taskData.notes || []);
        setImages(taskData.images || []);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load task data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [taskId]);

  const getMediaUrl = useCallback(
    (item, index, isThumbnail = true) => {
      if (taskId && index !== undefined) {
        const thumbnailParam = isThumbnail && isImage(item) ? '?thumbnail=true&width=200' : '';
        return `/api/media/construction/task/${taskId}/${index}${thumbnailParam}`;
      }
      return null;
    },
    [taskId]
  );

  const getFullImageUrl = useCallback(
    (item, index) => getMediaUrl(item, index, false),
    [getMediaUrl]
  );

  useEffect(() => {
    if (loading) return;
    if (images.filter((m) => isImage(m)).length === 0) return;

    const controller = new AbortController();
    const objectUrls = new Set();
    const container = viewerContainerRef.current;
    const pending = new Map();
    let requestedIndex = null;
    let viewerScript;
    const initViewer = () => {
      if (controller.signal.aborted) return;
      if (!window.Viewer || !viewerContainerRef.current) return;
      if (viewerInstanceRef.current) {
        viewerInstanceRef.current.destroy();
        viewerInstanceRef.current = null;
      }
      const imgs = viewerContainerRef.current.querySelectorAll('img[data-viewer-image="true"]');
      if (imgs.length === 0) return;

      viewerInstanceRef.current = new window.Viewer(viewerContainerRef.current, {
        inline: false,
        navbar: true,
        title: true,
        filter: (image) => image.dataset.viewerImage === 'true',
        toolbar: {
          zoomIn: true,
          zoomOut: true,
          oneToOne: true,
          reset: true,
          prev: true,
          play: false,
          next: true,
          rotateLeft: true,
          rotateRight: true,
          flipHorizontal: false,
          flipVertical: false,
        },
        url(image) {
          const full = image.getAttribute('data-full-src');
          if (full) return full;
          const srcAttr = image.getAttribute('src');
          if (srcAttr && srcAttr.includes('thumbnail=true')) {
            return srcAttr.replace(/\?thumbnail=true&width=\d+/, '');
          }
          return srcAttr;
        },
        view(event) {
          const { originalImage, index } = event.detail;
          requestedIndex = index;
          if (originalImage.dataset.fullSrc) return;
          event.preventDefault();
          if (pending.has(originalImage)) return;
          setMediaError(null);
          const request = constructionRequest(originalImage.dataset.fullUrl, { signal: controller.signal })
            .then((response) => response.blob())
            .then((blob) => {
              if (controller.signal.aborted) return;
              const url = URL.createObjectURL(blob);
              objectUrls.add(url);
              originalImage.dataset.fullSrc = url;
              const viewer = viewerInstanceRef.current;
              // ViewerJS caches original URLs in its navigation items.
              viewer?.update();
              if (requestedIndex === index) viewer?.view(index);
            })
            .catch((err) => {
              if (!controller.signal.aborted) {
                setMediaError(err.message || 'Unable to load image');
                viewerInstanceRef.current?.hide();
              }
            })
            .finally(() => pending.delete(originalImage));
          pending.set(originalImage, request);
        },
        hide() { requestedIndex = null; },
        loading: true,
      });
    };

    const loadViewerJS = () => {
      if (!document.getElementById('viewerjs-css')) {
        const link = document.createElement('link');
        link.id = 'viewerjs-css';
        link.rel = 'stylesheet';
        link.href = '/viewerjs/viewer.min.css';
        document.head.appendChild(link);
      }
      if (window.Viewer) {
        initViewer();
        return;
      }
      viewerScript = document.getElementById('viewerjs-js');
      const needsScript = !viewerScript;
      if (needsScript) {
        viewerScript = document.createElement('script');
        viewerScript.id = 'viewerjs-js';
        viewerScript.src = '/viewerjs/viewer.min.js';
      }
      viewerScript.addEventListener('load', initViewer);
      if (needsScript) document.head.appendChild(viewerScript);
    };

    loadViewerJS();

    return () => {
      controller.abort();
      viewerScript?.removeEventListener('load', initViewer);
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
      container?.querySelectorAll("img[data-full-src]").forEach((img) => { delete img.dataset.fullSrc; });
      if (viewerInstanceRef.current) {
        viewerInstanceRef.current.destroy();
        viewerInstanceRef.current = null;
      }
    };
  }, [loading, images, groups]);

  const renderMediaItem = (item, mediaIndexInTask) => {
    const itemIsVideo = isVideo(item);
    const itemIsImage = isImage(item);

    if (itemIsVideo) {
      return (
        <div
          key={`v-${mediaIndexInTask}`}
          className="relative cursor-pointer group flex-shrink-0"
          onClick={() => setActiveVideo(getMediaUrl(item, mediaIndexInTask, false))}
        >
          <div
            className="flex items-center justify-center"
            style={{
              width: 140,
              height: 100,
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
              borderRadius: 6,
            }}
          >
            <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
              <FaPlay size={18} color="#1a1a2e" style={{ marginLeft: 3 }} />
            </div>
          </div>
          <div className="text-[11px] mt-1 flex items-center gap-1 text-gray-600">
            <FaVideo size={10} color="#6366f1" />
            <span>Video</span>
          </div>
        </div>
      );
    }

    if (itemIsImage) {
      // Always use same-origin Flask proxy — never browser-direct private S3 URLs.
      const thumbSrc = getMediaUrl(item, mediaIndexInTask, true);
      const fullSrc = getFullImageUrl(item, mediaIndexInTask);

      return (
        <div key={`i-${mediaIndexInTask}`} className="flex-shrink-0">
          <AuthenticatedImage
            src={thumbSrc}
            data-full-url={fullSrc}
            data-viewer-image="true"
            alt={item.description || 'Task image'}
            style={{
              width: 140,
              height: 100,
              objectFit: 'cover',
              cursor: 'pointer',
              background: '#f5f5f5',
              borderRadius: 6,
            }}
          />
        </div>
      );
    }

    return (
      <div
        key={`u-${mediaIndexInTask}`}
        className="text-xs text-gray-500 border border-dashed border-gray-300 rounded p-2"
        style={{ width: 140, height: 100 }}
      >
        Unknown format
      </div>
    );
  };

  if (loading) return <div className="p-4">Loading...</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;

  const empty = (notes || []).length === 0 && (images || []).length === 0;

  return (
    <div
      style={{
        maxHeight: '80vh',
        minWidth: 350,
        maxWidth: 960,
        overflowY: 'auto',
        padding: 24,
        position: 'relative',
        background: 'white',
        borderRadius: 12,
        boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
      }}
    >
      <h2 className="text-2xl font-bold mb-4">Update</h2>
      {mediaError && <p role="alert" className="text-red-600 mb-3">{mediaError}</p>}

      {empty ? (
        <div className="text-gray-400">No notes or media for this task.</div>
      ) : (
        <div ref={viewerContainerRef} className="space-y-4">
          {groups.map((group, gi) => (
            <div
              key={`batch-${gi}-${group.startT ?? 'undated'}`}
              className="border border-gray-200 rounded-lg p-4 bg-gray-50/80"
            >
              <div className="text-sm font-semibold text-gray-700 mb-3 border-b border-gray-200 pb-2">
                {formatGroupTime(group.startT)}
              </div>

              {group.mediaItems.length > 0 && (
                <div className="flex flex-wrap gap-3 mb-3">
                  {group.mediaItems.map(({ img, index }) => (
                    <div key={`${gi}-m-${index}`}>{renderMediaItem(img, index)}</div>
                  ))}
                </div>
              )}

              {group.notes.length > 0 && (
                <div className="space-y-2">
                  {group.notes.map((note, ni) => (
                    <div key={`${gi}-n-${ni}-${note.date ?? ''}`} className="text-sm text-gray-800 whitespace-pre-wrap">
                      {note.text}
                      {(note.user_name || note.user_id) && (
                        <span className="block text-xs text-gray-500 mt-1">
                          {note.user_name || note.user_id}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

            </div>
          ))}
        </div>
      )}

      {activeVideo && (
        <VideoPlayerModal videoUrl={activeVideo} onClose={() => setActiveVideo(null)} />
      )}
    </div>
  );
};

export default ConTaskUpdateList;
